export type VsLinkType = 'simple' | 'next-link';

export const DEFAULT_VS_LINK_TYPE: VsLinkType = 'simple';

// next/link-only props that must never reach a plain <a>. Modelled structurally
// (as `unknown`) so this stays framework-free — the host app owns the real
// next/link types.
type NextLinkOnlyProps = {
  as?: unknown;
  replace?: unknown;
  scroll?: unknown;
  shallow?: unknown;
  passHref?: unknown;
  prefetch?: unknown;
  locale?: unknown;
  legacyBehavior?: unknown;
  onNavigate?: unknown;
};

type VsLinkInput = { type?: VsLinkType; href?: string } & Partial<NextLinkOnlyProps>;

type ResolveVsLinkResult<P extends VsLinkInput> =
  | { useNextLink: true; nextLinkProps: Omit<P, 'type'> }
  | {
      useNextLink: false;
      anchorProps: Omit<P, 'type' | 'href' | keyof NextLinkOnlyProps>;
      href: string | undefined;
    };

/**
 * Decide whether a link renders as a client-navigating `next/link` or a plain
 * `<a>`, and hand back the props cleaned for whichever element wins (next-only
 * props are dropped from the `<a>` branch). The host app owns the render; this
 * keeps the routing decision framework-free. Defaults to a plain `<a>`.
 */
export function resolveVsLink<P extends VsLinkInput>(props: P): ResolveVsLinkResult<P> {
  const useNextLink = (props.type ?? DEFAULT_VS_LINK_TYPE) === 'next-link';

  if (useNextLink) {
    const { type: _type, ...nextLinkProps } = props;
    return { useNextLink: true, nextLinkProps };
  }

  const {
    type: _type,
    as: _as,
    replace: _replace,
    scroll: _scroll,
    shallow: _shallow,
    passHref: _passHref,
    prefetch: _prefetch,
    locale: _locale,
    legacyBehavior: _legacyBehavior,
    onNavigate: _onNavigate,
    href,
    ...anchorProps
  } = props;

  // Preserve the original guard: a falsy href (e.g. '' from `link ?? ''`) yields
  // no href attribute — an inert <a> — rather than href="", which would be a
  // live self-navigating link to the current page.
  return { useNextLink: false, anchorProps, href: href ? href : undefined };
}
